import { useEffect, useState } from "react";
import { Link, useBlocker, useParams } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  ArrowLeft, Check, CreditCard, FileText, Loader2, Mail, MapPin, MoreHorizontal, Package, Pencil,
  Plus, Printer, RefreshCw, Send, Share2, ShoppingCart, TriangleAlert, Truck, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CityCombobox } from "@/components/address/city-combobox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OrderItemsEditor } from "@/components/wholesale/order-items-editor";
import { PaymentForm } from "@/components/wholesale/payment-form";
import { WholesaleStatusBadge, StockBadge } from "@/components/wholesale/status-badge";
import { TotalsPanel } from "@/components/wholesale/totals-panel";
import { OrderTimeline } from "@/components/wholesale/order-timeline";
import {
  LOCKED, EMAIL_KINDS, EMAIL_KIND_LABEL, STOCK_JOB_STATUS_LABEL, METHOD_LABEL, SHIPPING_FIELDS,
} from "@/components/wholesale/constants";
import {
  useWholesaleOrder, useUpdateWholesaleOrder, useSetWholesaleItems, useOrderAction,
  usePartner, useDeletePayment, type OrderAction,
} from "@/hooks/use-wholesale";
import { editorTotals, fromOrderItems, toItemInputs, type EditorItem } from "@/lib/wholesale-items";
import { ShareOrderDialog } from "@/components/wholesale/share-dialog";
import { CourierShipmentDialog } from "@/components/wholesale/courier-shipment-dialog";
import { dueAtFor, fmtKm, fmtUnixDate, fmtUnixDateTime } from "@/lib/wholesale-format";
import { stockProgress } from "@/lib/wholesale-stock-progress";
import { STOCK_UNTOUCHED_STATUSES, type WholesaleStatus } from "@/lib/wholesale";
import { cn } from "@/lib/utils";

const UNSAVED_HINT = "Prvo sačuvajte izmjene";

export function WholesaleOrderDetailPage() {
  const { orderId } = useParams({ strict: false }) as { orderId?: string };
  const { data: order, isLoading, error } = useWholesaleOrder(orderId);
  const partner = usePartner(order?.partnerId);
  const updateOrder = useUpdateWholesaleOrder();
  const setItemsMutation = useSetWholesaleItems();
  const action = useOrderAction();
  const deletePayment = useDeletePayment();

  const [items, setItems] = useState<EditorItem[]>([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  const [header, setHeader] = useState({
    shipping: { name: "", contact: "", phone: "", address: "", city: "", postalCode: "", country: "BA" },
    note: "",
    customerNote: "",
  });
  const [headerDirty, setHeaderDirty] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [shipOpen, setShipOpen] = useState(false);
  const [courierOpen, setCourierOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [repackOpen, setRepackOpen] = useState(false);
  const [editConfirmedOpen, setEditConfirmedOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const dirty = itemsDirty || headerDirty;

  // Čuvar nesačuvanih izmjena: zatvaranje kartice (beforeunload) i navigacija unutar aplikacije.
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  // Server je izvor istine; lokalna kopija se osvježava dok korisnik ne počne uređivati.
  useEffect(() => {
    if (!order || itemsDirty) return;
    setItems(fromOrderItems(order.items));
  }, [order, itemsDirty]);

  useEffect(() => {
    if (!order || headerDirty) return;
    setHeader({
      shipping: { ...order.shipping },
      note: order.note ?? "",
      customerNote: order.customerNote ?? "",
    });
  }, [order, headerDirty]);

  const status: WholesaleStatus = order?.status ?? "draft";
  const locked = LOCKED.includes(status);
  const totals = editorTotals(items, order?.vatRate ?? 0);

  async function runAction(act: OrderAction, body: Record<string, unknown>, okMessage: string): Promise<boolean> {
    try {
      const res = await action.mutateAsync({ id: orderId!, action: act, body });
      if (act === "confirm" && res?.warnings?.length && !body?.ignoreStockWarnings) {
        setWarnings(res.warnings);
        return false;
      }
      setWarnings([]);
      toast.success(okMessage);
      return true;
    } catch (e: any) {
      toast.error(e?.message || "Akcija nije uspjela");
      return false;
    }
  }

  async function handleSaveHeader() {
    try {
      await updateOrder.mutateAsync({ id: orderId!, ...header });
      setHeaderDirty(false);
      setEditingHeader(false);
      toast.success("Podaci narudžbe spremljeni");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju podataka");
    }
  }

  function cancelHeaderEdit() {
    // Vraćanje na serversku kopiju: efekat iznad ponovo puni formu čim dirty padne.
    setHeaderDirty(false);
    setEditingHeader(false);
    if (order) {
      setHeader({ shipping: { ...order.shipping }, note: order.note ?? "", customerNote: order.customerNote ?? "" });
    }
  }

  function discardAll() {
    cancelHeaderEdit();
    setItemsDirty(false);
    if (order) setItems(fromOrderItems(order.items));
  }

  async function saveAll() {
    if (headerDirty) await handleSaveHeader();
    if (itemsDirty) await handleSaveItems();
  }

  async function saveItems() {
    try {
      await setItemsMutation.mutateAsync({ id: orderId!, items: toItemInputs(items) });
      setItemsDirty(false);
      setRepackOpen(false);
      setEditConfirmedOpen(false);
      toast.success("Stavke spremljene");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju stavki");
    }
  }

  async function handleSaveItems() {
    if (status === "packed") {
      setRepackOpen(true);
      return;
    }
    if (status === "confirmed") {
      setEditConfirmedOpen(true);
      return;
    }
    await saveItems();
  }

  async function handleShip(e?: React.FormEvent) {
    e?.preventDefault();
    setShipOpen(false);
    const ok = await runAction("ship", { trackingNumber: trackingNumber.trim() }, "Narudžba označena kao poslana");
    if (ok) setTrackingNumber("");
  }

  async function handleDeletePayment() {
    const id = paymentToDelete;
    if (!id) return;
    try {
      await deletePayment.mutateAsync(id);
      toast.success("Uplata obrisana");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju uplate");
    } finally {
      setPaymentToDelete(null);
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <Link to="/wholesale/orders" className="text-xs text-muted-foreground hover:underline">← Narudžbe</Link>
        <p className={cn("text-xs", error ? "text-destructive" : "text-muted-foreground")}>
          {error ? (error as Error).message : "Narudžba nije pronađena."}
        </p>
      </div>
    );
  }

  const partnerLoading = partner.isLoading;
  const partnerEmail = partner.data?.email ?? "";
  const emailAllowed = !STOCK_UNTOUCHED_STATUSES.includes(status) && status !== "cancelled";
  const canSendEmail = emailAllowed && !partnerLoading && !!partnerEmail && !action.isPending && !dirty;
  const payments = order.payments ?? [];
  const paid = order.paid ?? 0;
  const hasUnavailable = items.some((it) => it.unavailable);
  // Server vraća emailove hronološki; ovdje se okreću da zadnji pokušaj bude na vrhu.
  const emailLog = [...(order.emails ?? [])].reverse();
  const actionBlocked = action.isPending || dirty;
  const hint = dirty ? UNSAVED_HINT : undefined;

  const remaining = Math.max(0, order.total - paid);
  const paidPct = order.total > 0 ? Math.min(100, (paid / order.total) * 100) : 0;
  const progress = stockProgress(order.stockJobs);
  const stockJobs = order.stockJobs ?? [];
  const shippingLines = [
    header.shipping.name,
    header.shipping.contact,
    header.shipping.phone,
    header.shipping.address,
    [header.shipping.postalCode, header.shipping.city].filter(Boolean).join(" "),
  ].filter((l) => l && l.trim());
  const hasDocs = !STOCK_UNTOUCHED_STATUSES.includes(status);
  // Naljepnica postoji samo za stvarno kreiranu kurirsku pošiljku; ručno upisan tracking
  // („Označi kao poslano“) nema pošiljku u bazi pa ni naljepnicu.
  const labelTracking = order.shipment?.status === "created" ? order.shipment.trackingNumber : null;

  const primaryAction = (() => {
    if (STOCK_UNTOUCHED_STATUSES.includes(status)) return { label: "Potvrdi", icon: Check, onClick: () => runAction("confirm", {}, "Narudžba potvrđena") };
    // Spakovana bez pošiljke: sljedeći korak je nalog u brzoj pošti (ručni unos ostaje u „Više akcija“).
    if (status === "packed" && !order.trackingNumber) return { label: "Nalog u brzoj pošti", icon: Truck, onClick: () => setCourierOpen(true) };
    if (status === "confirmed" || status === "packed") return { label: "Označi kao poslano", icon: Truck, onClick: () => setShipOpen(true) };
    if (status === "shipped") return { label: "Završi", icon: Check, onClick: () => runAction("complete", {}, "Narudžba završena") };
    return null;
  })();

  const openPdf = (kind: "predracun" | "otpremnica") =>
    window.open(`/api/wholesale/orders/${order.id}/${kind}.pdf`, "_blank");

  return (
    <div className="p-4 flex flex-col gap-3 pb-24">
      {/* Zaglavlje ostaje pri vrhu — status i akcije trebaju i na dnu duge narudžbe. */}
      <div className="sticky top-0 z-20 -mx-4 -mt-4 px-4 py-2.5 bg-background border-b flex flex-col gap-1.5">
        {/* Identitet: šta gledam i koliko je. */}
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon-xs" aria-label="Nazad na narudžbe" asChild>
            <Link to="/wholesale/orders">
              <ArrowLeft className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <h2 className="text-base font-medium tabular-nums shrink-0">{order.number}</h2>
          <WholesaleStatusBadge status={order.status} />
          <StockBadge state={order.stockState} />
          <span className="ml-auto text-base font-medium tabular-nums shrink-0" title="Ukupan iznos narudžbe">
            {fmtKm(totals.total)}
          </span>
        </div>

        {/* Kontekst lijevo, akcije desno — po težini: primarna, dijeljenje, dokumenti, ostalo. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 flex-wrap min-w-0 text-xs text-muted-foreground pl-7">
            <Link
              to="/wholesale/partners/$partnerId"
              params={{ partnerId: order.partnerId }}
              className="text-foreground hover:underline underline-offset-2 truncate max-w-[16rem]"
            >
              {order.partnerName}
            </Link>
            {/* Šifra se čita s partnera, ne iz snimka narudžbe — ručno se dopisuje i naknadno. */}
            {partner.data?.customerCode && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">Šifra kupca: {partner.data.customerCode}</span>
              </>
            )}
            <span aria-hidden>·</span>
            <span className="tabular-nums">{fmtUnixDate(order.createdAt)}</span>
            {order.trackingNumber && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">Pošiljka {order.trackingNumber}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {primaryAction && (
              <Button size="sm" className="gap-1.5 flex-1 sm:flex-none" disabled={actionBlocked} title={hint} onClick={primaryAction.onClick}>
                <primaryAction.icon className="h-3.5 w-3.5" />
                {primaryAction.label}
              </Button>
            )}
            {hasDocs && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5" disabled={dirty} title={hint} onClick={() => setShareOpen(true)}>
                  <Share2 className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Podijeli</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5" disabled={dirty} title={hint}>
                      <FileText className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Dokumenti</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onSelect={() => openPdf("predracun")}>
                      <FileText className="h-3.5 w-3.5" />
                      Predračun
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => openPdf("otpremnica")}>
                      <FileText className="h-3.5 w-3.5" />
                      Otpremnica
                    </DropdownMenuItem>
                    {labelTracking && (
                      <DropdownMenuItem onSelect={() => setCourierOpen(true)}>
                        <Printer className="h-3.5 w-3.5" />
                        Naljepnica
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {/* Otkazivanje je nepovratno — nikad jedan promašen klik od primarne akcije. */}
            {!locked && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon-sm" aria-label="Više akcija" disabled={dirty} title={hint}>
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {status === "packed" && !order.trackingNumber && (
                    <DropdownMenuItem disabled={action.isPending} onSelect={() => setShipOpen(true)}>
                      <Truck className="h-3.5 w-3.5" />
                      Označi kao poslano
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem variant="destructive" disabled={action.isPending} onSelect={() => setCancelOpen(true)}>
                    <X className="h-3.5 w-3.5" />
                    Otkaži narudžbu
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Tok narudžbe */}
      <div className="ring-1 ring-foreground/10 px-3 py-3 sm:px-4">
        <OrderTimeline
          status={status}
          stamps={{
            createdAt: order.createdAt, confirmedAt: order.confirmedAt, packedAt: order.packedAt,
            shippedAt: order.shippedAt, completedAt: order.completedAt, cancelledAt: order.cancelledAt,
          }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3 items-start">
        {/* Lijevo: stavke, zaliha, emailovi */}
        <div className="flex flex-col gap-3 min-w-0">
          {/* Stavke */}
          <section className="ring-1 ring-foreground/10" aria-labelledby="sec-items">
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b">
              <div className="flex items-center gap-1.5 min-w-0">
                <ShoppingCart className="h-3 w-3 text-muted-foreground" />
                <span id="sec-items" className="text-xs font-medium">Stavke</span>
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground tabular-nums">
                  {items.length} {items.length === 1 ? "artikal" : items.length >= 2 && items.length <= 4 ? "artikla" : "artikala"}
                </span>
              </div>
              {itemsDirty && (
                <span className="text-[10px] uppercase tracking-[0.08em] text-amber-600 dark:text-amber-400">Izmijenjeno</span>
              )}
            </div>
            <OrderItemsEditor
              framed={false}
              items={items}
              partnerDiscount={partner.data?.discountPct ?? 0}
              storeDiscounts={partner.data?.storeDiscounts}
              readOnly={locked}
              onChange={(next) => { setItemsDirty(true); setItems(next); }}
            />
            <div className="border-t px-3 py-3 flex flex-col gap-2">
              <TotalsPanel totals={totals} vatRate={order.vatRate} />
              {hasUnavailable && (
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Oznaka „Nedostupno" znači da stavke trenutno nema u mirroru — artikal je obrisan ili je shop
                  izbačen iz veleprodaje. Provjera se radi pri svakom otvaranju, pa i kod potvrđenih narudžbi.
                </p>
              )}
            </div>
          </section>

          {/* Zaliha */}
          <section className="ring-1 ring-foreground/10" aria-labelledby="sec-stock">
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b">
              <div className="flex items-center gap-1.5 min-w-0">
                <Package className="h-3 w-3 text-muted-foreground" />
                <span id="sec-stock" className="text-xs font-medium">Zaliha u shopu</span>
                {progress.active && (
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground tabular-nums">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Upisivanje {progress.done}/{progress.total}
                  </span>
                )}
              </div>
              {order.stockState === "failed" && (
                <Button variant="outline" size="xs" className="gap-1.5" disabled={action.isPending}
                  onClick={() => runAction("stock/retry", {}, "Upis zalihe ponovo pokrenut")}>
                  <RefreshCw className="h-3 w-3" />
                  Pokušaj ponovo
                </Button>
              )}
            </div>
            {stockJobs.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">
                {STOCK_UNTOUCHED_STATUSES.includes(status) ? "Zaliha se rezerviše tek kad se narudžba potvrdi." : "Nema upisa zalihe."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                      <th className="py-2 px-3 text-left font-medium">SKU</th>
                      <th className="py-2 px-2 text-right font-medium w-20">Promjena</th>
                      <th className="py-2 px-3 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockJobs.map((j) => {
                      const isActive = j.id === progress.activeId;
                      const failed = j.status === "failed";
                      return (
                        <tr key={j.id} className={cn("border-b last:border-b-0", isActive && "bg-primary/5")}>
                          <td className="py-2 px-3 tabular-nums font-medium">{j.sku || "—"}</td>
                          <td className={cn("py-2 px-2 text-right tabular-nums", j.delta > 0 && "text-emerald-700 dark:text-emerald-400")}>
                            {j.delta > 0 ? `+${j.delta}` : j.delta}
                          </td>
                          <td className="py-2 px-3">
                            {isActive ? (
                              <span className="flex items-center gap-1.5 text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Upisivanje…
                              </span>
                            ) : (
                              <div className="flex flex-col">
                                <span className={cn("flex items-center gap-1.5", failed ? "text-destructive" : "text-muted-foreground")}>
                                  {j.status === "done" && <Check className="h-3 w-3" />}
                                  {STOCK_JOB_STATUS_LABEL[j.status] ?? j.status}
                                </span>
                                {j.lastError && <span className="text-[11px] text-destructive/80 break-words">{j.lastError}</span>}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Emailovi */}
          <section className="ring-1 ring-foreground/10" aria-labelledby="sec-emails">
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b">
              <div className="flex items-center gap-1.5 min-w-0">
                <Mail className="h-3 w-3 text-muted-foreground" />
                <span id="sec-emails" className="text-xs font-medium">Emailovi partneru</span>
              </div>
              {partnerLoading ? (
                <Skeleton className="h-3 w-32" />
              ) : partnerEmail ? (
                <span className="text-[11px] text-muted-foreground truncate">{partnerEmail}</span>
              ) : null}
            </div>
            {partner.error && (
              <p className="px-3 pt-3 text-xs text-destructive">Podaci partnera nisu učitani: {(partner.error as Error).message}</p>
            )}
            {!partnerLoading && !partner.error && !partnerEmail && (
              <p className="px-3 pt-3 text-xs text-muted-foreground">Partner nema email, pa se poruke ne mogu slati.</p>
            )}
            {emailLog.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">
                {emailAllowed ? "Nijedan email još nije poslan." : "Emailovi partneru dostupni su tek nakon potvrde narudžbe."}
              </p>
            ) : (
              <div className="divide-y divide-foreground/10">
                {emailLog.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium">{EMAIL_KIND_LABEL[e.kind] ?? e.kind}</span>
                      <span className={cn("truncate tabular-nums", e.error ? "text-destructive" : "text-muted-foreground")}>
                        {e.error ? `Greška: ${e.error}` : e.sentAt ? `Poslano ${fmtUnixDateTime(e.sentAt)}` : "U redu za slanje"}
                      </span>
                    </div>
                    <Button variant="ghost" size="xs" className="gap-1.5 shrink-0" disabled={!canSendEmail} title={hint}
                      onClick={() => runAction(`emails/${e.kind}`, {}, "Email poslan")}>
                      <Send className="h-3 w-3" />
                      <span className="hidden sm:inline">Pošalji ponovo</span>
                      <span className="sm:hidden">Ponovo</span>
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {emailAllowed && (
              <div className="border-t px-3 py-2.5 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Pošalji ručno</span>
                {EMAIL_KINDS.map(({ kind, label }) => (
                  <Button key={kind} variant="outline" size="xs" className="gap-1.5" disabled={!canSendEmail} title={hint}
                    onClick={() => runAction(`emails/${kind}`, {}, "Email poslan")}>
                    <Send className="h-3 w-3" />
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Desno: iznos i plaćanje, dostava, uplate */}
        <div className="flex flex-col gap-3 min-w-0">
          {/* Sažetak plaćanja */}
          <section className="ring-1 ring-foreground/10" aria-labelledby="sec-summary">
            <div className="flex items-center gap-1.5 px-3 py-2.5 border-b">
              <CreditCard className="h-3 w-3 text-muted-foreground" />
              <span id="sec-summary" className="text-xs font-medium">Plaćanje</span>
            </div>
            <div className="p-3 flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Ukupno</span>
                <span className="text-lg font-medium tabular-nums leading-none">{fmtKm(order.total)}</span>
              </div>
              <div className="h-1.5 w-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={Math.round(paidPct)} aria-valuemin={0} aria-valuemax={100} aria-label="Plaćeni dio">
                <div className="h-full bg-foreground transition-all duration-300" style={{ width: `${paidPct}%` }} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Plaćeno</span>
                  <span className="tabular-nums">{fmtKm(paid)}</span>
                </div>
                <div className="flex flex-col items-end text-right">
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Preostalo</span>
                  <span className={cn("tabular-nums", remaining > 0 ? "font-medium" : "text-muted-foreground")}>{fmtKm(remaining)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Dostava i napomene */}
          <section className="ring-1 ring-foreground/10" aria-labelledby="sec-shipping">
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3 text-muted-foreground" />
                <span id="sec-shipping" className="text-xs font-medium">Dostava i napomene</span>
              </div>
              {!locked && !editingHeader && (
                <Button variant="ghost" size="icon-xs" aria-label="Uredi dostavu i napomene" onClick={() => setEditingHeader(true)}>
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
              {editingHeader && (
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="icon-xs" aria-label="Sačuvaj" disabled={!headerDirty || updateOrder.isPending} onClick={handleSaveHeader}>
                    {updateOrder.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  </Button>
                  <Button variant="ghost" size="icon-xs" aria-label="Odustani" disabled={updateOrder.isPending} onClick={cancelHeaderEdit}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
            {editingHeader ? (
              <div className="p-3 flex flex-col gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                  {SHIPPING_FIELDS.map(([key, label]) => (
                    <div key={key} className={cn("flex flex-col gap-1", key === "name" && "sm:col-span-2 lg:col-span-1")}>
                      <Label htmlFor={`order-shipping-${key}`} className="text-xs">{label}</Label>
                      {key === "city" ? (
                        <CityCombobox
                          id={`order-shipping-${key}`}
                          city={header.shipping.city ?? ""}
                          postalCode={header.shipping.postalCode ?? ""}
                          onSelect={(city, postalCode) => {
                            setHeaderDirty(true);
                            setHeader((h) => ({ ...h, shipping: { ...h.shipping, city, postalCode } }));
                          }}
                        />
                      ) : (
                        <Input
                          id={`order-shipping-${key}`}
                          className="h-8 text-xs"
                          autoFocus={key === "name"}
                          value={header.shipping[key] ?? ""}
                          onChange={(e) => {
                            setHeaderDirty(true);
                            setHeader((h) => ({ ...h, shipping: { ...h.shipping, [key]: e.target.value } }));
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="order-note" className="text-xs">Interna napomena</Label>
                  <Textarea id="order-note" className="text-xs min-h-16" value={header.note}
                    onChange={(e) => { setHeaderDirty(true); setHeader((h) => ({ ...h, note: e.target.value })); }} />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="order-customer-note" className="text-xs">Napomena za otpremnicu</Label>
                  <Textarea id="order-customer-note" className="text-xs min-h-16" value={header.customerNote}
                    onChange={(e) => { setHeaderDirty(true); setHeader((h) => ({ ...h, customerNote: e.target.value })); }} />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" size="sm" disabled={updateOrder.isPending} onClick={cancelHeaderEdit}>Odustani</Button>
                  <Button size="sm" disabled={!headerDirty || updateOrder.isPending} onClick={handleSaveHeader}>
                    {updateOrder.isPending ? "Spremanje…" : "Sačuvaj"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-foreground/10 text-xs">
                <div className="px-3 py-2.5 flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground mb-0.5">Adresa</span>
                  {shippingLines.length === 0 ? (
                    <span className="text-muted-foreground">Adresa nije upisana.</span>
                  ) : (
                    shippingLines.map((l, i) => (
                      <span key={i} className={cn(i === 0 ? "font-medium" : "text-foreground/80", "tabular-nums")}>{l}</span>
                    ))
                  )}
                </div>
                <div className="px-3 py-2.5 flex flex-col gap-0.5">
                  {header.note ? (
                    <div className="flex flex-col gap-1 border border-red-600/40 bg-red-600/10 px-2.5 py-2 text-red-900 dark:border-red-500/50 dark:bg-red-500/15 dark:text-red-100">
                      <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-red-700 dark:text-red-300">
                        <TriangleAlert className="h-3 w-3" />
                        Interna napomena
                      </span>
                      <span className="whitespace-pre-wrap text-sm font-medium">{header.note}</span>
                    </div>
                  ) : (
                    <>
                      <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Interna napomena</span>
                      <span className="text-muted-foreground">—</span>
                    </>
                  )}
                </div>
                <div className="px-3 py-2.5 flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Napomena za otpremnicu</span>
                  <span className={cn("whitespace-pre-wrap", !header.customerNote && "text-muted-foreground")}>{header.customerNote || "—"}</span>
                </div>
              </div>
            )}
          </section>

          {/* Uplate */}
          <section className="ring-1 ring-foreground/10" aria-labelledby="sec-payments">
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b">
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-3 w-3 text-muted-foreground" />
                <span id="sec-payments" className="text-xs font-medium">Uplate</span>
                {payments.length > 0 && (
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground tabular-nums">{payments.length}</span>
                )}
              </div>
              {!paymentOpen && (
                <Button variant="outline" size="xs" className="gap-1" onClick={() => setPaymentOpen(true)}>
                  <Plus className="h-3 w-3" />
                  Nova uplata
                </Button>
              )}
            </div>
            {paymentOpen && (
              <div className="p-3 border-b bg-muted/30">
                <PaymentForm
                  embedded
                  partnerId={order.partnerId}
                  defaultOrderId={order.id}
                  onDone={() => setPaymentOpen(false)}
                  onCancel={() => setPaymentOpen(false)}
                />
              </div>
            )}
            {payments.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">Još nema evidentiranih uplata.</p>
            ) : (
              <div className="divide-y divide-foreground/10">
                {payments.map((pay) => (
                  <div key={pay.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="tabular-nums font-medium">{fmtKm(pay.amount)}</span>
                      <span className="text-muted-foreground truncate tabular-nums">
                        {fmtUnixDate(pay.paidAt)} · {METHOD_LABEL[pay.method] ?? pay.method}
                        {pay.note ? ` · ${pay.note}` : ""}
                      </span>
                    </div>
                    <Button variant="ghost" size="icon-xs" aria-label="Obriši uplatu" disabled={deletePayment.isPending}
                      onClick={() => setPaymentToDelete(pay.id)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Traka za nesačuvane izmjene: jedno mjesto za spremanje, vidljivo bez skrolanja. */}
      {dirty && (
        <div
          role="status"
          className="fixed bottom-0 inset-x-0 z-30 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-2.5"
        >
          <div className="flex items-center justify-between gap-3 max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 min-w-0">
              <span className="size-2 bg-amber-500 shrink-0" aria-hidden />
              <span className="text-xs truncate">
                <span className="font-medium">Nesačuvane izmjene</span>
                <span className="text-muted-foreground hidden sm:inline"> — akcije nad narudžbom su isključene dok ne sačuvaš.</span>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" disabled={updateOrder.isPending || setItemsMutation.isPending} onClick={discardAll}>
                Odbaci
              </Button>
              <Button size="sm" disabled={updateOrder.isPending || setItemsMutation.isPending} onClick={saveAll}>
                {updateOrder.isPending || setItemsMutation.isPending ? "Spremanje…" : "Sačuvaj"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upozorenja pri potvrdi */}
      <AlertDialog open={warnings.length > 0} onOpenChange={(v) => !v && setWarnings([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zaliha nije dovoljna</AlertDialogTitle>
            <AlertDialogDescription>
              Narudžba nije potvrđena. Provjeri stavke ispod pa odluči.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="text-xs list-disc pl-5 flex flex-col gap-1">
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction
              disabled={action.isPending}
              onClick={() => runAction("confirm", { ignoreStockWarnings: true }, "Narudžba potvrđena")}
            >
              Potvrdi svejedno
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Otkazivanje */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Otkazati narudžbu?</AlertDialogTitle>
            <AlertDialogDescription>
              {STOCK_UNTOUCHED_STATUSES.includes(status)
                ? "Narudžba se označava kao otkazana i više se ne može uređivati. Zaliha nije bila rezervisana."
                : "Rezervisana zaliha se vraća, a započeta priprema se briše."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction
              disabled={action.isPending}
              onClick={() => { setCancelOpen(false); runAction("cancel", {}, "Narudžba otkazana"); }}
            >
              Otkaži narudžbu
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Izmjena stavki potvrđene narudžbe */}
      <AlertDialog open={editConfirmedOpen} onOpenChange={setEditConfirmedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Narudžba je već potvrđena</AlertDialogTitle>
            <AlertDialogDescription>
              Spremanje stavki mijenja rezervisanu zalihu u shopu prema novim količinama, a partner dobija
              email „Izmjena narudžbe" s novim predračunom.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction disabled={setItemsMutation.isPending} onClick={saveItems}>
              Sačuvaj stavke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Izmjena stavki spakovane narudžbe */}
      <AlertDialog open={repackOpen} onOpenChange={setRepackOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Narudžba je spakovana</AlertDialogTitle>
            <AlertDialogDescription>
              Izmjena stavki vraća narudžbu u status „Potvrđena" i pick ide ispočetka. Zaliha u shopu se
              usklađuje s novim količinama, a partner dobija email „Izmjena narudžbe" s novim predračunom.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction disabled={setItemsMutation.isPending} onClick={saveItems}>
              Sačuvaj stavke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Brisanje uplate */}
      <AlertDialog open={paymentToDelete !== null} onOpenChange={(v) => !v && setPaymentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Obrisati uplatu?</AlertDialogTitle>
            <AlertDialogDescription>
              Uplata se briše trajno, a saldo partnera i stanje plaćanja narudžbe se ponovo obračunavaju.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction disabled={deletePayment.isPending} onClick={handleDeletePayment}>
              Obriši uplatu
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Napuštanje stranice s nesačuvanim izmjenama */}
      <AlertDialog open={blocker.status === "blocked"} onOpenChange={(v) => !v && blocker.reset?.()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nesačuvane izmjene</AlertDialogTitle>
            <AlertDialogDescription>
              Imate izmjene koje nisu sačuvane. Ako napustite stranicu, izmjene se gube.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Ostani</AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>Napusti bez spremanja</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Slanje */}
      <ShareOrderDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        order={{ id: order.id, number: order.number, partnerName: order.partnerName, total: order.total }}
        dueAt={dueAtFor(order.confirmedAt, partner.data?.paymentTermDays ?? 0)}
        partnerEmail={partnerEmail}
      />

      <CourierShipmentDialog
        open={courierOpen}
        onOpenChange={setCourierOpen}
        localId={order.localId}
        orderNumber={order.number}
        trackingNumber={labelTracking}
        pendingShipment={order.shipment?.status === "pending" ? order.shipment : null}
      />

      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent className="sm:max-w-sm">
          <form onSubmit={handleShip} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Označi kao poslano</DialogTitle>
              <DialogDescription>Broj pošiljke nije obavezan.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1">
              <Label htmlFor="order-tracking-number" className="text-xs">Broj pošiljke</Label>
              <Input
                id="order-tracking-number"
                className="h-8 text-xs"
                autoFocus
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
              />
            </div>
            <DialogFooter showCloseButton={false}>
              <Button type="button" variant="outline" size="sm" onClick={() => setShipOpen(false)}>Odustani</Button>
              <Button type="submit" size="sm" disabled={action.isPending}>Označi kao poslano</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
